import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ArrowIosDownward, CheckMark } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { Stack, Typography } from '@lyearn/ui';
import AutocompleteSearch from '@/components/AutocompleteSearch';
import { PopperOverride } from '@/components/PopperOverride';
import { DepartmentFragment } from '@/modules/org-hierarchy/modules/departments/graphql/fragments/generated/Department';
import { useDepartmentQuery } from '@/modules/org-hierarchy/modules/departments/graphql/queries/generated/department';
import useDepartments from '@/modules/org-hierarchy/modules/departments/hooks/useDepartments';

import { autocompleteClasses } from '../../../styles';
import Loader from '../../Loader';
import { filterValueStyle } from '../styles';
import { FilterValueProps } from '../types';

const renderDepartment =
  (selectedDepartmentId?: string | null) => (department: DepartmentFragment) => {
    const isSelected = selectedDepartmentId === department.id;

    return (
      <Stack alignItems="center" className="mb-2 w-full py-10 px-12" justifyContent="between">
        <Typography
          className="truncate"
          color={isSelected ? 'text-text-primary' : 'text-text-secondary'}
          variant="body-short-02">
          {department.name}
        </Typography>
        {isSelected ? <CheckMark className="h-20 w-20 ms-12" fillColorToken="icon-active" /> : null}
      </Stack>
    );
  };

// todo make autocomplete with JSON input to avoid repeatation of code
function DepartmentFilter(props: FilterValueProps) {
  const { t } = useTranslation();
  const [searchStr, setSearchStr] = useState('');
  const { filterValue, onFilterValueChange, disabled, error, className } = props;
  const {
    data: departments,
    isLoading,
    fetchMore,
  } = useDepartments({
    searchText: searchStr,
  });
  const [{ data: selectedDepartment, fetching: fetchingSelected }] = useDepartmentQuery({
    variables: { id: filterValue! },
    pause: !filterValue,
  });
  const onSelect = useCallback(
    (departmentId?: string) => {
      onFilterValueChange(departmentId);
    },
    [onFilterValueChange],
  );
  const hasError = useMemo(
    () => error && !disabled && !filterValue,
    [disabled, error, filterValue],
  );

  if (isLoading || fetchingSelected) {
    return <Loader className={cn(filterValueStyle, className)} />;
  }

  return (
    <Stack
      className={cn(filterValueStyle, className, {
        'cursor-not-allowed': disabled,
      })}
      direction="col">
      <AutocompleteSearch
        PopperComponent={(val) => <PopperOverride placement="bottom-end" {...val} />}
        className="flex-1"
        classes={autocompleteClasses}
        closeIcon={null}
        data={departments}
        data-id="tag-and-properties"
        disabled={disabled}
        error={hasError}
        fetchMore={fetchMore}
        initialValue={selectedDepartment?.department?.name}
        placeholder={t('Group:Editor.Value')}
        popupIcon={<ArrowIosDownward className="h-20 w-20" fillColorToken="icon-placeholder" />}
        renderers={{
          renderOption: renderDepartment(filterValue),
        }}
        onChange={onSelect}
        onInputChange={setSearchStr}
      />
      {hasError ? (
        <Typography className="mt-8" color="text-text-error" variant="body-short-03">
          {t('Common:Form.Error.ValueCantBeEmpty')}
        </Typography>
      ) : null}
    </Stack>
  );
}

export default DepartmentFilter;
